/* eslint-disable no-console */
/**
 * Centralized logging service for vfloow
 * Replaces console.log/error/warn with structured logging
 *
 * In production:
 * - No debug logs are output
 * - Errors are sent to monitoring service
 * - Structured JSON output for log aggregation
 *
 * In development:
 * - Pretty formatted output
 * - All log levels visible
 *
 * IMPORTANT: Uses lazy initialization to avoid circular dependency issues
 */

// Lazy getters to avoid initialization issues
let _isDevelopment: boolean | undefined;
let _isTest: boolean | undefined;

const isDevelopment = () => {
  if (_isDevelopment === undefined) {
    _isDevelopment = import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === 'development';
  }
  return _isDevelopment;
};

const isTest = () => {
  if (_isTest === undefined) {
    _isTest = import.meta.env.MODE === 'test';
  }
  return _isTest;
};

// Simple noop function
const noop = () => {};

// Export a simplified logger interface that matches console API
// Using arrow functions with lazy evaluation to avoid TDZ errors
export const logger = {
  /**
   * Debug level logging - only visible in development
   * Use for detailed debugging information
   */
  debug: (...args: any[]) => {
    if (isDevelopment() && !isTest()) {
      console.debug(...args);
    }
  },

  /**
   * Info level logging - visible in production
   * Use for important informational messages
   */
  info: (...args: any[]) => {
    if (!isTest()) {
      console.info(...args);
    }
  },

  /**
   * Warning level logging - visible in production
   * Use for warning messages that don't require immediate action
   */
  warn: (...args: any[]) => {
    if (!isTest()) {
      console.warn(...args);
    }
  },

  /**
   * Error level logging - always visible (except in tests)
   * Use for error messages and exceptions
   */
  error: (...args: any[]) => {
    if (!isTest()) {
      console.error(...args);
    }
  },

  /**
   * Legacy log method - maps to debug
   * @deprecated Use logger.debug instead
   */
  log: (...args: any[]) => {
    if (isDevelopment() && !isTest()) {
      console.debug(...args);
    }
  },

  /**
   * Table logging - only for development
   * Falls back to regular logging in production
   */
  table: (data: any) => {
    if (isDevelopment() && !isTest()) {
      console.table(data);
    }
  },

  /**
   * Group logging - for development only
   */
  group: (label?: string) => {
    if (isDevelopment() && !isTest()) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (isDevelopment() && !isTest()) {
      console.groupEnd();
    }
  },

  /**
   * Time tracking - for performance debugging
   */
  time: (label: string) => {
    if (isDevelopment() && !isTest()) {
      console.time(label);
    }
  },

  timeEnd: (label: string) => {
    if (isDevelopment() && !isTest()) {
      console.timeEnd(label);
    }
  },
};

// For backward compatibility during migration
export default logger;

// Type definitions for better TypeScript support
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type Logger = typeof logger;
